import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, ArrowLeft, Eye, EyeOff, KeyRound } from 'lucide-react';
import { auth } from '../services/api';

const schema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordForm = z.infer<typeof schema>;

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      await auth.resetPassword(token, data.newPassword);
      navigate('/login', { state: { passwordReset: true } });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Reset link is invalid or has expired.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--nx-navy)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'var(--font-body)',
      }}
    >
      {/* Grid background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(26,108,246,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,108,246,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />

      {/* Radial glow */}
      <div
        style={{
          position: 'absolute',
          width: '600px',
          height: '500px',
          background:
            'radial-gradient(ellipse at 50% 40%, rgba(26,108,246,0.14) 0%, rgba(109,40,217,0.07) 40%, transparent 70%)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Card */}
      <div
        className="nx-anim-fade-up"
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 420,
          background: 'var(--nx-surface)',
          borderRadius: 20,
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 40px 120px rgba(0,0,0,0.5), 0 0 0 1px rgba(26,108,246,0.08)',
          overflow: 'hidden',
        }}
      >
        {/* Top gradient bar */}
        <div style={{ height: 3, background: 'linear-gradient(90deg, #1A6CF6, #6D28D9)' }} />

        <div style={{ padding: '36px 32px' }}>
          {/* Wordmark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
            <div
              style={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #1A6CF6, #6D28D9)',
                borderRadius: 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14, color: '#fff' }}>
                Nx
              </span>
            </div>
            <div>
              <div
                style={{
                  fontFamily: 'Syne, sans-serif',
                  fontWeight: 800,
                  fontSize: 16,
                  color: 'var(--nx-text-primary)',
                  letterSpacing: '-0.02em',
                }}
              >
                NEXORA
              </div>
              <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', letterSpacing: '0.08em' }}>
                Pentagon Analytics
              </div>
            </div>
          </div>

          {/* No token — invalid link */}
          {!token ? (
            <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 16,
                  marginBottom: 28,
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AlertCircle size={24} style={{ color: '#EF4444' }} />
                </div>
                <div>
                  <h1
                    style={{
                      fontFamily: 'Syne, sans-serif',
                      fontSize: 22,
                      fontWeight: 800,
                      color: 'var(--nx-text-primary)',
                      letterSpacing: '-0.03em',
                      marginBottom: 8,
                    }}
                  >
                    Invalid link
                  </h1>
                  <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.6 }}>
                    This reset link is missing or invalid. Please request a new one.
                  </p>
                </div>
              </div>
              <Link
                to="/forgot-password"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  fontSize: 13,
                  color: 'var(--nx-blue)',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                Request a new reset link
              </Link>
            </>
          ) : (
            /* Form state */
            <>
              <div style={{ marginBottom: 28 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: 'rgba(26,108,246,0.1)',
                    border: '1px solid rgba(26,108,246,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16,
                  }}
                >
                  <KeyRound size={20} style={{ color: '#1A6CF6' }} />
                </div>
                <h1
                  style={{
                    fontFamily: 'Syne, sans-serif',
                    fontSize: 24,
                    fontWeight: 800,
                    color: 'var(--nx-text-primary)',
                    letterSpacing: '-0.03em',
                    marginBottom: 6,
                  }}
                >
                  Set new password
                </h1>
                <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
                  Choose a strong password of at least 8 characters.
                </p>
              </div>

              {error && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 14px',
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    borderRadius: 10,
                    marginBottom: 20,
                  }}
                >
                  <AlertCircle size={15} style={{ color: '#EF4444', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#EF4444' }}>{error}</span>
                </div>
              )}

              <form
                onSubmit={handleSubmit(onSubmit)}
                style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                noValidate
              >
                {/* New password */}
                <div>
                  <label
                    htmlFor="newPassword"
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--nx-text-secondary)',
                      marginBottom: 6,
                      letterSpacing: '0.04em',
                    }}
                  >
                    NEW PASSWORD
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="newPassword"
                      type={showNew ? 'text' : 'password'}
                      autoComplete="new-password"
                      autoFocus
                      placeholder="At least 8 characters"
                      {...register('newPassword')}
                      style={{
                        width: '100%',
                        padding: '11px 42px 11px 14px',
                        background: 'var(--nx-surface-2)',
                        border: `1px solid ${errors.newPassword ? 'rgba(239,68,68,0.5)' : 'var(--nx-border)'}`,
                        borderRadius: 10,
                        fontSize: 14,
                        color: 'var(--nx-text-primary)',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 150ms',
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = '#1A6CF6')}
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor = errors.newPassword
                          ? 'rgba(239,68,68,0.5)'
                          : 'var(--nx-border)')
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew((v) => !v)}
                      aria-label={showNew ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--nx-text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        padding: 0,
                      }}
                    >
                      {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.newPassword.message}</p>
                  )}
                </div>

                {/* Confirm password */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--nx-text-secondary)',
                      marginBottom: 6,
                      letterSpacing: '0.04em',
                    }}
                  >
                    CONFIRM PASSWORD
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="confirmPassword"
                      type={showConfirm ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Re-enter your password"
                      {...register('confirmPassword')}
                      style={{
                        width: '100%',
                        padding: '11px 42px 11px 14px',
                        background: 'var(--nx-surface-2)',
                        border: `1px solid ${errors.confirmPassword ? 'rgba(239,68,68,0.5)' : 'var(--nx-border)'}`,
                        borderRadius: 10,
                        fontSize: 14,
                        color: 'var(--nx-text-primary)',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 150ms',
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = '#1A6CF6')}
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor = errors.confirmPassword
                          ? 'rgba(239,68,68,0.5)'
                          : 'var(--nx-border)')
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--nx-text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        padding: 0,
                      }}
                    >
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.confirmPassword.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="nx-btn nx-btn-primary"
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    padding: '12px',
                    fontSize: 14,
                    fontWeight: 600,
                    marginTop: 4,
                    opacity: isLoading ? 0.7 : 1,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isLoading ? (
                    <>
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#fff',
                          borderRadius: '50%',
                          animation: 'nx-spin 0.7s linear infinite',
                          display: 'inline-block',
                        }}
                      />
                      Resetting...
                    </>
                  ) : (
                    'Reset password'
                  )}
                </button>
              </form>

              <p style={{ marginTop: 24, textAlign: 'center', fontSize: 12, color: 'var(--nx-text-muted)' }}>
                <Link
                  to="/login"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    color: 'var(--nx-blue)',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  <ArrowLeft size={12} /> Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 2, marginTop: 24, fontSize: 11, color: 'var(--nx-text-muted)' }}>
        © {new Date().getFullYear()} Pentagon Analytics. All rights reserved.
      </div>
    </div>
  );
};

export default ResetPasswordPage;
